import { Component, OnInit } from "@angular/core";
import { AccountService } from "@onsip/common/services/api/resources/account/account.service";
import { PackageInvoiceItem } from "@onsip/common/services/api/resources/accountInvoice/account-invoice-item";
import {
  AccountInvoice,
  AccountInvoiceService
} from "@onsip/common/services/api/resources/accountInvoice/account-invoice.service";
import { GetPrimaryPackageNameService } from "@onsip/common/services/api/resources/package/get-primary-package-name";
import {
  DisplayPackageName,
  PAY_AS_YOU_GO_PLAN
} from "@onsip/common/services/api/resources/package/package";
import { views } from "@onsip/web/app/phone/views";
import { currencyFormatter } from "@onsip/web/features/shared/helpers/currency-formatter";
import { combineLatest, map, startWith } from "rxjs";

@Component({
  selector: "onsip-account-information",
  templateUrl: "./account-information.component.html",
  styleUrl: "./account-information.component.scss",
  standalone: false
})
export class AccountInformationComponent implements OnInit {
  readonly monthCost = "$0.00";
  readonly monthCostText = "/month per account";
  views = views;

  private invoice$ = this.accountInvoiceService.accountState.pipe(
    map(accounts => accounts.find(account => account.accountInvoiceId === "0"))
  );

  private basicInvoice$ = this.invoice$.pipe(
    map(invoice => invoice?.itemGroups.find(items => items.type === "Package")),
    map(packages =>
      (packages?.item as Array<PackageInvoiceItem>)?.find(
        item => item.packageItem.packageDescription === PAY_AS_YOU_GO_PLAN
      )
    )
  );

  account$ = this.accountService.accountState.pipe(map(accountState => accountState[0]));
  accountPaymentDate$ = this.invoice$.pipe(map(invoice => invoice?.date));

  accountBalance$ = this.account$.pipe(map(account => currencyFormatter(account.receivable)));

  accountPSTNCredit$ = this.account$.pipe(map(account => currencyFormatter(account.balance)));

  isNegativeAccountPSTNCredit$ = this.account$.pipe(
    map(account => parseFloat(account.balance) < 0)
  );

  currentPlan$ = this.getPrimaryPackageNameService.getDisplayName();

  enableAutoRefill$ = this.account$.pipe(map(account => account.recharge));

  monthCost$ = combineLatest({
    currentPlan: this.currentPlan$,
    unlimitedInvoice: this.invoice$,
    basicInvoice: this.basicInvoice$
  }).pipe(
    map(({ currentPlan, unlimitedInvoice, basicInvoice }) => {
      if (currentPlan === DisplayPackageName.UNLIMITED_PLAN) {
        return currencyFormatter(this.getUnlimitedPricingPerUser(unlimitedInvoice));
      } else if (currentPlan === DisplayPackageName.BASIC_PLAN) {
        return currencyFormatter(basicInvoice ? basicInvoice.amount : "0");
      } else {
        return this.monthCost;
      }
    }),
    startWith(this.monthCost)
  );

  monthCostText$ = this.currentPlan$.pipe(
    map(currentPlan =>
      currentPlan === DisplayPackageName.UNLIMITED_PLAN ? "/month per user" : "/month per account"
    ),
    startWith(this.monthCostText)
  );

  constructor(
    private accountInvoiceService: AccountInvoiceService,
    private accountService: AccountService,
    private getPrimaryPackageNameService: GetPrimaryPackageNameService
  ) {}

  ngOnInit(): void {
    this.accountInvoiceService.accountInvoiceRead();
  }

  private getUnlimitedPricingPerUser(unlimitedPlan: AccountInvoice | undefined): number {
    if (unlimitedPlan) {
      const packages = unlimitedPlan.itemGroups.find(item => item.type === "Package");
      const userPackage = (packages?.item as Array<PackageInvoiceItem>)?.find(item =>
        item.packageItem.packageName.match(/User-1\.[4567]-(\d+)/)
      );
      if (userPackage) {
        const numOfUser = parseInt(
          userPackage.packageItem.packageName.replace(/User-1\.[4567]-/, "")
        );
        const userPerPrice = parseFloat(userPackage.amount) / (numOfUser < 5 ? 5 : numOfUser); // we charge a minimum of 5 users for unlimited plan;
        return parseFloat(userPerPrice.toFixed(2));
      }
    }

    return 0;
  }
}
